import React, { useState, useRef, useEffect, useCallback } from 'react';
import { markersAPI } from '../../services/api';
import useGoogleMaps from '../../hooks/useGoogleMaps';
import useMapWithLocation from '../../hooks/useMapWithLocation';
import { getCategoriesByGroup, CATEGORY_GROUPS, getCategoryConfig } from '../../config/categories';
import { getLucideMarkerIcon } from '../../utils/lucideToSvg';
import './MarkerCreationModal.css';

const EMPTY_FORM = {
  name: '',
  description: '',
  category: 'food',
  tags: '',
  openHours: '',
  imageUrl: '',
  dietaryOptions: {
    vegetarian: false,
    vegan: false,
    glutenFree: false,
    kosher: false,
    halal: false
  },
  allergens: []
};

// Offset in degrees (~15 metres) applied when pasting a copied marker
const PASTE_OFFSET = 0.00015;

const MarkerCreationModal = ({ isOpen, onClose, mapData, onMarkersUpdated }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const clickListenerRef = useRef(null);
  const existingMarkersRef = useRef([]);          // google.maps.Marker instances
  const existingMarkersDataRef = useRef([]);       // parallel array of marker data objects
  const { isLoaded, loadError } = useGoogleMaps();
  const [markers, setMarkers] = useState([]);
  const [currentMarker, setCurrentMarker] = useState(null);
  const [showMarkerForm, setShowMarkerForm] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);

  // Edit mode
  const [editingMarker, setEditingMarker] = useState(null);

  // ✨ NEW: copy/paste state
  const [copiedMarker, setCopiedMarker] = useState(null);  // marker data object
  const [isPasteMode, setIsPasteMode] = useState(false);

  // ✨ NEW: toast notification
  const [toast, setToast] = useState(null);  // { message, type }
  const toastTimerRef = useRef(null);

  const {
    userLocation,
    locationError,
    isLoadingLocation,
    updateUserMarkerOnMap,
    cleanupMarkers
  } = useMapWithLocation({
    enableTracking: isOpen,
    updateInterval: 3000,
    minDistanceChange: 3,
    showAccuracyCircle: true,
    highAccuracy: true
  });

  const [markerFormData, setMarkerFormData] = useState(EMPTY_FORM);

  const groupedCategories = getCategoriesByGroup();

  const allergenOptions = [
    { value: 'gluten', label: 'Gluten', icon: '🌾' },
    { value: 'nuts', label: 'Tree Nuts', icon: '🥜' },
    { value: 'peanuts', label: 'Peanuts', icon: '🥜' },
    { value: 'dairy', label: 'Dairy', icon: '🥛' },
    { value: 'eggs', label: 'Eggs', icon: '🥚' },
    { value: 'soy', label: 'Soy', icon: '🫘' },
    { value: 'fish', label: 'Fish', icon: '🐟' },
    { value: 'shellfish', label: 'Shellfish', icon: '🦐' },
    { value: 'sesame', label: 'Sesame', icon: '🌰' }
  ];

  // ── Helpers ──────────────────────────────────────────────────────────────

  const showToast = useCallback((message, type = 'success') => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ message, type });
    toastTimerRef.current = setTimeout(() => setToast(null), 3000);
  }, []);

  const isFoodCategory = useCallback((category) => {
    const foodCategories = ['food', 'drinks', 'alcohol', 'coffee', 'dessert', 'food_truck'];
    return foodCategories.includes(category);
  }, []);

  const fetchMarkers = useCallback(async () => {
    if (!mapData?.id) return;
    try {
      const response = await markersAPI.getMarkers(mapData.id);
      setMarkers(response.data.data.markers);
    } catch (err) {
      console.error('❌ Error fetching markers:', err);
      setError('Failed to load existing markers');
    }
  }, [mapData?.id]);

  const getMarkerIconConfig = useCallback((category) => {
    const config = getCategoryConfig(category);
    return getLucideMarkerIcon(config.icon, config.color, 24);
  }, []);

  const createInfoWindowContent = useCallback((marker, isEditMode = false) => {
    const config = getCategoryConfig(marker.category);

    const dietaryBadges = Object.entries(marker.dietaryOptions || {})
      .filter(([, value]) => value)
      .map(([key]) => {
        const labels = {
          vegetarian: '🥬 Vegetarian', vegan: '🌱 Vegan',
          glutenFree: '🌾 Gluten-Free', kosher: '✡️ Kosher', halal: '☪️ Halal'
        };
        return `<span style="background:#34a853;color:white;padding:2px 6px;border-radius:10px;font-size:0.7rem;margin:2px;">${labels[key] || key}</span>`;
      }).join('');

    const allergenBadges = (marker.allergens || []).map(allergen => {
      const allergenMap = {
        gluten: '🌾 Gluten', nuts: '🥜 Nuts', peanuts: '🥜 Peanuts',
        dairy: '🥛 Dairy', eggs: '🥚 Eggs', soy: '🫘 Soy',
        fish: '🐟 Fish', shellfish: '🦐 Shellfish', sesame: '🌰 Sesame'
      };
      return `<span style="background:#dc2626;color:white;padding:2px 6px;border-radius:10px;font-size:0.7rem;margin:2px;">⚠️ ${allergenMap[allergen] || allergen}</span>`;
    }).join('');

    const actionButtons = isEditMode ? `
      <div style="margin-top:1rem;display:flex;gap:0.5rem;justify-content:center;flex-wrap:wrap;">
        <button onclick="window.editMarker('${marker.id}')"
          style="background:linear-gradient(135deg,#1a73e8,#1557b0);color:white;border:none;padding:0.5rem 1rem;border-radius:6px;cursor:pointer;font-size:0.8rem;font-weight:600;">
          ✏️ Edit
        </button>
        <button onclick="window.copyMarker('${marker.id}')"
          style="background:linear-gradient(135deg,#f59e0b,#d97706);color:white;border:none;padding:0.5rem 1rem;border-radius:6px;cursor:pointer;font-size:0.8rem;font-weight:600;">
          📋 Copy
        </button>
        <button onclick="window.deleteMarker('${marker.id}')"
          style="background:linear-gradient(135deg,#dc2626,#b91c1c);color:white;border:none;padding:0.5rem 1rem;border-radius:6px;cursor:pointer;font-size:0.8rem;font-weight:600;">
          🗑️ Delete
        </button>
      </div>
    ` : '';

    return `
      <div style="padding:0.5rem;max-width:260px;">
        <h3 style="margin:0 0 0.5rem 0;color:#374151;">${marker.name}</h3>
        ${marker.description ? `<p style="margin:0 0 0.5rem 0;color:#6b7280;font-size:0.875rem;">${marker.description}</p>` : ''}
        ${marker.openHours ? `<p style="margin:0 0 0.25rem 0;font-size:0.8rem;"><strong>⏰ Hours:</strong> ${marker.openHours}</p>` : ''}
        <p style="margin:0 0 0.25rem 0;font-size:0.8rem;color:${config.color}"><strong>📂 Category:</strong> ${config.emoji} ${config.label}</p>
        ${dietaryBadges ? `<div style="margin:0.5rem 0;">${dietaryBadges}</div>` : ''}
        ${allergenBadges ? `<div style="margin:0.5rem 0;"><strong style="font-size:0.75rem;color:#dc2626;">Contains:</strong><br>${allergenBadges}</div>` : ''}
        ${marker.tags?.length ? `<div style="margin-top:0.5rem;">${marker.tags.map(tag => `<span style="background:#e5e7eb;color:#374151;padding:2px 6px;border-radius:6px;font-size:0.7rem;margin:2px;">#${tag}</span>`).join('')}</div>` : ''}
        ${actionButtons}
        ${isEditMode ? `<p style="margin:0.75rem 0 0;font-size:0.7rem;color:#9ca3af;text-align:center;">↕️ Drag marker to reposition</p>` : ''}
      </div>
    `;
  }, []);

  // ── Map click handler ─────────────────────────────────────────────────────

  // Use a ref so the paste handler always sees the latest isPasteMode / copiedMarker
  // without needing to re-register the click listener every time.
  const isPasteModeRef = useRef(false);
  const copiedMarkerRef = useRef(null);

  useEffect(() => { isPasteModeRef.current = isPasteMode; }, [isPasteMode]);
  useEffect(() => { copiedMarkerRef.current = copiedMarker; }, [copiedMarker]);

  const handleMapClick = useCallback((event) => {
    if (editingMarker) return;

    const lat = event.latLng.lat();
    const lng = event.latLng.lng();

    const withinBounds = () => {
      if (!mapData?.area?.bounds) return true;
      const { south, north, west, east } = mapData.area.bounds;
      return lat >= south && lat <= north && lng >= west && lng <= east;
    };

    if (!withinBounds()) {
      setError('Please click within the event area (blue highlighted zone)');
      setTimeout(() => setError(null), 3000);
      return;
    }

    // ✨ Paste mode: ignore normal form flow, paste the copied marker instead
    if (isPasteModeRef.current && copiedMarkerRef.current) {
      pasteMarkerAt(lat, lng);
      return;
    }

    setCurrentMarker({ lat, lng });
    setShowMarkerForm(true);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapData?.area?.bounds, editingMarker]);

  // ── Drag & drop ───────────────────────────────────────────────────────────

  /**
   * Called when a map marker is dropped at a new position.
   * Auto-saves the new coordinates to the API immediately.
   */
  const handleMarkerDragEnd = useCallback(async (markerId, newLat, newLng) => {
    try {
      await markersAPI.updateMarker(markerId, {
        coordinates: [newLng, newLat]   // GeoJSON: [lng, lat]
      });
      setMarkers(prev =>
        prev.map(m =>
          m.id === markerId
            ? { ...m, coordinates: [newLng, newLat] }
            : m
        )
      );
      showToast('📍 Marker position saved!');
      if (onMarkersUpdated) onMarkersUpdated();
    } catch (err) {
      console.error('❌ Error saving dragged marker:', err);
      showToast('❌ Failed to save new position', 'error');
    }
  }, [onMarkersUpdated, showToast]);

  // ── Copy / paste ──────────────────────────────────────────────────────────

  const copyMarker = useCallback((markerId) => {
    const marker = markers.find(m => m.id === markerId);
    if (!marker) return;
    setCopiedMarker(marker);
    setIsPasteMode(true);
    showToast(`📋 Copied "${marker.name}" — click map to paste`);
  }, [markers, showToast]);

  const pasteMarkerAt = useCallback(async (lat, lng) => {
    const source = copiedMarkerRef.current;
    if (!source || !mapData) return;

    try {
      setIsCreating(true);
      const pastedData = {
        mapId: mapData.id,
        name: `${source.name} (copy)`,
        description: source.description || '',
        coordinates: [lng + PASTE_OFFSET, lat + PASTE_OFFSET],
        category: source.category,
        tags: source.tags || [],
        openHours: source.openHours || '',
        imageUrl: source.imageUrl || '',
        dietaryOptions: source.dietaryOptions || EMPTY_FORM.dietaryOptions,
        allergens: source.allergens || []
      };

      const response = await markersAPI.createMarker(pastedData);
      setMarkers(prev => [...prev, response.data.data.marker]);
      showToast(`✅ Pasted "${source.name}" nearby!`);
      if (onMarkersUpdated) onMarkersUpdated();
    } catch (err) {
      console.error('❌ Error pasting marker:', err);
      showToast('❌ Failed to paste marker', 'error');
    } finally {
      setIsCreating(false);
    }
  }, [mapData, onMarkersUpdated, showToast]);

  const cancelPasteMode = useCallback(() => {
    setIsPasteMode(false);
    setCopiedMarker(null);
  }, []);

  // ── Map markers rendering ─────────────────────────────────────────────────

  const clearExistingMarkers = useCallback(() => {
    existingMarkersRef.current.forEach(m => m.setMap(null));
    existingMarkersRef.current = [];
    existingMarkersDataRef.current = [];
  }, []);

  const displayMarkersOnMap = useCallback(() => {
    const map = mapInstanceRef.current;
    if (!map || !markers.length) return;

    clearExistingMarkers();

    markers.forEach((markerData) => {
      const iconConfig = getMarkerIconConfig(markerData.category);
      const mapMarker = new window.google.maps.Marker({
        position: { lat: markerData.coordinates[1], lng: markerData.coordinates[0] },
        map,
        title: markerData.name,
        icon: iconConfig,
        draggable: true,   // ✨ enable drag
        cursor: 'grab'
      });

      const infoWindow = new window.google.maps.InfoWindow({
        content: createInfoWindowContent(markerData, true)
      });

      // Open info window on click
      mapMarker.addListener('click', () => infoWindow.open(map, mapMarker));

      // ✨ Auto-save on drag end
      mapMarker.addListener('dragend', (event) => {
        const newLat = event.latLng.lat();
        const newLng = event.latLng.lng();
        handleMarkerDragEnd(markerData.id, newLat, newLng);
      });

      // ✨ Visual feedback while dragging
      mapMarker.addListener('dragstart', () => {
        infoWindow.close();
      });

      existingMarkersRef.current.push(mapMarker);
      existingMarkersDataRef.current.push(markerData);
    });
  }, [markers, clearExistingMarkers, getMarkerIconConfig, createInfoWindowContent, handleMarkerDragEnd]);

  // ── Edit marker ───────────────────────────────────────────────────────────

  const openEditForm = useCallback((markerId) => {
    const marker = markers.find(m => m.id === markerId);
    if (!marker) return;

    setEditingMarker(marker);
    setMarkerFormData({
      name: marker.name || '',
      description: marker.description || '',
      category: marker.category || 'food',
      tags: (marker.tags || []).join(', '),
      openHours: marker.openHours || '',
      imageUrl: marker.imageUrl || '',
      dietaryOptions: marker.dietaryOptions || EMPTY_FORM.dietaryOptions,
      allergens: marker.allergens || []
    });
    setCurrentMarker(null);
    setShowMarkerForm(true);
    setError(null);
  }, [markers]);

  const updateMarker = useCallback(async () => {
    if (!editingMarker) return;
    if (!markerFormData.name.trim()) { setError('Marker name is required'); return; }

    try {
      setIsCreating(true);
      setError(null);

      const updateData = {
        name: markerFormData.name,
        description: markerFormData.description,
        category: markerFormData.category,
        tags: markerFormData.tags.split(',').map(t => t.trim()).filter(Boolean),
        openHours: markerFormData.openHours,
        imageUrl: markerFormData.imageUrl,
        dietaryOptions: markerFormData.dietaryOptions,
        allergens: markerFormData.allergens
      };

      const response = await markersAPI.updateMarker(editingMarker.id, updateData);
      const updated = response.data.data.marker;

      setMarkers(prev => prev.map(m => m.id === editingMarker.id ? { ...m, ...updated } : m));
      setEditingMarker(null);
      setMarkerFormData(EMPTY_FORM);
      setShowMarkerForm(false);
      showToast('✅ Marker updated!');
      if (onMarkersUpdated) onMarkersUpdated();
    } catch (err) {
      console.error('❌ Error updating marker:', err);
      setError(err.response?.data?.message || 'Failed to update marker');
    } finally {
      setIsCreating(false);
    }
  }, [editingMarker, markerFormData, onMarkersUpdated, showToast]);

  // ── Map initialisation ────────────────────────────────────────────────────

  useEffect(() => {
    if (!isLoaded || !isOpen || !mapRef.current || !mapData) return;
    if (mapInstanceRef.current) return;

    try {
      const center = mapData.area?.center
        ? { lat: mapData.area.center.lat, lng: mapData.area.center.lng }
        : { lat: 32.0853, lng: 34.7818 };

      const map = new window.google.maps.Map(mapRef.current, {
        center,
        zoom: 16,
        mapTypeId: window.google.maps.MapTypeId.ROADMAP,
        styles: [{ featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }]
      });

      mapInstanceRef.current = map;

      if (mapData.area?.coordinates) {
        const path = mapData.area.coordinates.map(coord => ({ lat: coord[1], lng: coord[0] }));
        new window.google.maps.Polygon({
          paths: path,
          strokeColor: '#1a73e8', strokeOpacity: 0.8, strokeWeight: 3,
          fillColor: '#1a73e8', fillOpacity: 0.1, clickable: false
        }).setMap(map);

        const bounds = new window.google.maps.LatLngBounds();
        path.forEach(point => bounds.extend(point));
        map.fitBounds(bounds);
      }

      clickListenerRef.current = map.addListener('click', handleMapClick);
      fetchMarkers();
    } catch (err) {
      console.error('❌ Error initializing map:', err);
      setError('Failed to initialize map');
    }

    return () => {
      if (clickListenerRef.current) {
        window.google.maps.event.removeListener(clickListenerRef.current);
        clickListenerRef.current = null;
      }
      clearExistingMarkers();
      mapInstanceRef.current = null;
    };
  }, [isLoaded, isOpen, mapData, handleMapClick, fetchMarkers, clearExistingMarkers]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !userLocation) return;
    updateUserMarkerOnMap(map);
    return () => { if (!isOpen) cleanupMarkers(); };
  }, [userLocation, updateUserMarkerOnMap, cleanupMarkers, isOpen]);

  useEffect(() => {
    displayMarkersOnMap();
  }, [displayMarkersOnMap]);

  // ── Expose global handlers for info window buttons ────────────────────────

  useEffect(() => {
    window.deleteMarker = async (markerId) => {
      if (!window.confirm('Delete this marker? This cannot be undone.')) return;
      try {
        await markersAPI.deleteMarker(markerId);
        setMarkers(prev => prev.filter(m => m.id !== markerId));
        showToast('🗑️ Marker deleted');
        if (onMarkersUpdated) onMarkersUpdated();
      } catch (err) {
        console.error('❌ Error deleting marker:', err);
        setError(err.response?.data?.message || 'Failed to delete marker');
      }
    };
    window.editMarker = openEditForm;
    window.copyMarker = copyMarker;

    return () => {
      delete window.deleteMarker;
      delete window.editMarker;
      delete window.copyMarker;
    };
  }, [openEditForm, copyMarker, onMarkersUpdated, showToast]);

  // ── Create marker ─────────────────────────────────────────────────────────

  const addMarker = useCallback(async () => {
    if (!currentMarker || !mapData) { setError('No marker position selected'); return; }
    if (!markerFormData.name.trim()) { setError('Marker name is required'); return; }

    try {
      setIsCreating(true);
      setError(null);

      const markerData = {
        mapId: mapData.id,
        name: markerFormData.name,
        description: markerFormData.description,
        coordinates: [currentMarker.lng, currentMarker.lat],
        category: markerFormData.category,
        tags: markerFormData.tags.split(',').map(t => t.trim()).filter(Boolean),
        openHours: markerFormData.openHours,
        imageUrl: markerFormData.imageUrl,
        dietaryOptions: markerFormData.dietaryOptions,
        allergens: markerFormData.allergens
      };

      const response = await markersAPI.createMarker(markerData);
      setMarkers(prev => [...prev, response.data.data.marker]);
      setMarkerFormData(EMPTY_FORM);
      setCurrentMarker(null);
      setShowMarkerForm(false);
      if (onMarkersUpdated) onMarkersUpdated();
    } catch (err) {
      console.error('❌ Error creating marker:', err);
      setError(err.response?.data?.message || 'Failed to create marker');
    } finally {
      setIsCreating(false);
    }
  }, [currentMarker, mapData, markerFormData, onMarkersUpdated]);

  // ── Form helpers ──────────────────────────────────────────────────────────

  const handleMarkerInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith('dietary_')) {
      const key = name.replace('dietary_', '');
      setMarkerFormData(prev => ({ ...prev, dietaryOptions: { ...prev.dietaryOptions, [key]: checked } }));
    } else if (name.startsWith('allergen_')) {
      const allergen = name.replace('allergen_', '');
      setMarkerFormData(prev => ({
        ...prev,
        allergens: checked ? [...prev.allergens, allergen] : prev.allergens.filter(a => a !== allergen)
      }));
    } else {
      setMarkerFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }
  }, []);

  const closeForm = useCallback(() => {
    setShowMarkerForm(false);
    setEditingMarker(null);
    setCurrentMarker(null);
    setMarkerFormData(EMPTY_FORM);
    setError(null);
  }, []);

  const handleClose = useCallback(() => {
    cleanupMarkers();
    closeForm();
    cancelPasteMode();
    setIsCreating(false);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    onClose();
  }, [cleanupMarkers, closeForm, cancelPasteMode, onClose]);

  // ── Early returns ─────────────────────────────────────────────────────────

  if (!isOpen) return null;

  if (loadError) {
    return (
      <div className="marker-creation-overlay">
        <div className="marker-creation-modal">
          <div className="marker-creation-error">
            <h3>🗺️ Marker Creation Error</h3>
            <p>{loadError}</p>
            <button onClick={handleClose} className="btn-primary">Close</button>
          </div>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="marker-creation-overlay">
        <div className="marker-creation-modal">
          <div className="marker-creation-loading">
            <div className="loading-spinner"></div>
            <p>Loading Marker Creator...</p>
          </div>
        </div>
      </div>
    );
  }

  const isEditMode = !!editingMarker;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="marker-creation-overlay" onClick={handleClose}>
      <div className="marker-creation-modal" onClick={e => e.stopPropagation()}>

        {/* ── Toast notification ── */}
        {toast && (
          <div className={`marker-toast marker-toast--${toast.type}`}>
            {toast.message}
          </div>
        )}

        <div className="marker-creation-header">
          <h2 className="marker-creation-title">📍 Add Markers to "{mapData?.title}"</h2>
          <div className="marker-stats">
            <span className="marker-count">{markers.length} markers added</span>
          </div>
          <button className="marker-creation-close" onClick={handleClose}>×</button>
        </div>

        <div className="marker-creation-content">
          <div className="marker-instructions">
            <h3>🖱️ Click anywhere on the map to add a marker</h3>
            <p>Click on the blue highlighted area to place points of interest for your event.</p>

            {isLoadingLocation && (
              <div className="location-status loading">
                <span className="spinner-small"></span> Getting your location...
              </div>
            )}
            {locationError && (
              <div className="location-status warning">⚠️ Location: {locationError}</div>
            )}

            <div className="click-help">
              <div className="help-item">💡 <strong>Tip:</strong> Click within the blue area to add markers</div>
              <div className="help-item">↕️ <strong>Drag:</strong> Drag any marker to reposition it (auto-saves)</div>
              <div className="help-item">📋 <strong>Copy:</strong> Copy a marker then click map to paste nearby</div>
              <div className="help-item">📍 <strong>Blue Dot:</strong> Your current location (live tracking)</div>
              <div className="help-item">📌 <strong>Current markers:</strong> {markers.length}</div>
              {userLocation?.accuracy && (
                <div className="help-item">🎯 <strong>GPS Accuracy:</strong> ±{Math.round(userLocation.accuracy)}m</div>
              )}
            </div>

            {/* ✨ Paste mode banner */}
            {isPasteMode && copiedMarker && (
              <div className="paste-mode-banner">
                <span>📋 Paste mode — click anywhere on map to paste <strong>"{copiedMarker.name}"</strong> nearby</span>
                <button onClick={cancelPasteMode} className="paste-cancel-btn">✕ Cancel</button>
              </div>
            )}
          </div>

          <div className="marker-map-container">
            <div ref={mapRef} className="marker-creation-map" />

            {markers.length > 0 && (
              <div className="marker-legend">
                <h4>Current Markers:</h4>
                <div className="marker-list">
                  {markers.map((marker, index) => {
                    const config = getCategoryConfig(marker.category);
                    const IconComponent = config.icon;
                    return (
                      <div key={index} className="marker-item">
                        <IconComponent color={config.color} size={20} strokeWidth={2} />
                        <span className="marker-name">{marker.name}</span>
                        <span className="marker-category">({config.label})</span>
                        <div className="marker-item-actions">
                          <button
                            onClick={() => openEditForm(marker.id)}
                            className="marker-edit-btn"
                            title="Edit marker"
                          >✏️</button>
                          {/* ✨ Copy button */}
                          <button
                            onClick={() => copyMarker(marker.id)}
                            className="marker-copy-btn"
                            title="Copy marker"
                          >📋</button>
                          <button
                            onClick={async () => {
                              if (!window.confirm('Delete this marker? This cannot be undone.')) return;
                              try {
                                await markersAPI.deleteMarker(marker.id);
                                setMarkers(prev => prev.filter(m => m.id !== marker.id));
                                showToast('🗑️ Marker deleted');
                                if (onMarkersUpdated) onMarkersUpdated();
                              } catch (err) {
                                setError('Failed to delete marker');
                              }
                            }}
                            className="marker-delete-btn"
                            title="Delete marker"
                          >🗑️</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className="marker-creation-error">
              <p>❌ {error}</p>
            </div>
          )}
        </div>

        {/* ── Marker form (create OR edit) ── */}
        {showMarkerForm && (
          <div className="marker-form-overlay">
            <div className="marker-form">
              <div className="marker-form-header">
                <h4>{isEditMode ? '✏️ Edit Marker' : '📍 Add Marker'}</h4>
                {!isEditMode && currentMarker && (
                  <small>Position: {currentMarker.lat.toFixed(6)}, {currentMarker.lng.toFixed(6)}</small>
                )}
                {isEditMode && (
                  <small>Editing: <strong>{editingMarker.name}</strong></small>
                )}
                <button onClick={closeForm} className="marker-form-close">×</button>
              </div>

              <div className="marker-form-content">
                <div className="form-group">
                  <label htmlFor="marker-name">Name *</label>
                  <input
                    type="text" id="marker-name" name="name"
                    value={markerFormData.name} onChange={handleMarkerInputChange}
                    placeholder="e.g., Amazing Falafel Stand"
                    maxLength={100} required className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="marker-category">Category *</label>
                  <select
                    id="marker-category" name="category"
                    value={markerFormData.category} onChange={handleMarkerInputChange}
                    className="form-input"
                  >
                    {CATEGORY_GROUPS.map(groupName => (
                      <optgroup key={groupName} label={groupName}>
                        {groupedCategories[groupName]?.map(cat => (
                          <option key={cat.key} value={cat.key}>{cat.emoji} {cat.label}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="marker-description">Description</label>
                  <textarea
                    id="marker-description" name="description"
                    value={markerFormData.description} onChange={handleMarkerInputChange}
                    placeholder="Describe this location (optional)"
                    maxLength={500} rows={2} className="form-textarea"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="marker-hours">Hours</label>
                    <input
                      type="text" id="marker-hours" name="openHours"
                      value={markerFormData.openHours} onChange={handleMarkerInputChange}
                      placeholder="e.g., 10:00-22:00" className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="marker-tags">Tags</label>
                    <input
                      type="text" id="marker-tags" name="tags"
                      value={markerFormData.tags} onChange={handleMarkerInputChange}
                      placeholder="vegan, popular, new" className="form-input"
                    />
                  </div>
                </div>

                {isFoodCategory(markerFormData.category) && (
                  <>
                    <div className="dietary-section">
                      <label>Dietary Options:</label>
                      <div className="dietary-options">
                        {Object.keys(markerFormData.dietaryOptions).map(option => (
                          <label key={option} className="dietary-checkbox">
                            <input
                              type="checkbox" name={`dietary_${option}`}
                              checked={markerFormData.dietaryOptions[option]}
                              onChange={handleMarkerInputChange}
                            />
                            <span>{option.charAt(0).toUpperCase() + option.slice(1)}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="dietary-section">
                      <label>⚠️ Contains Allergens:</label>
                      <div className="dietary-options">
                        {allergenOptions.map(allergen => (
                          <label key={allergen.value} className="dietary-checkbox allergen-checkbox">
                            <input
                              type="checkbox" name={`allergen_${allergen.value}`}
                              checked={markerFormData.allergens.includes(allergen.value)}
                              onChange={handleMarkerInputChange}
                            />
                            <span>{allergen.icon} {allergen.label}</span>
                          </label>
                        ))}
                      </div>
                      <small style={{ color: '#6b7280', fontSize: '0.75rem', fontStyle: 'italic', marginTop: '0.5rem', display: 'block' }}>
                        Select allergens that this food contains to help visitors with allergies
                      </small>
                    </div>
                  </>
                )}

                <div className="form-actions">
                  <button onClick={closeForm} className="btn-cancel" disabled={isCreating}>
                    Cancel
                  </button>
                  <button
                    onClick={isEditMode ? updateMarker : addMarker}
                    className="btn-add-marker"
                    disabled={isCreating || !markerFormData.name.trim()}
                  >
                    {isCreating
                      ? (isEditMode ? '💾 Saving...' : '📍 Adding...')
                      : (isEditMode ? '💾 Save Changes' : '📍 Add Marker')
                    }
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="marker-creation-footer">
          <div className="footer-info">
            <span>💡 Drag markers to reposition • 📋 Copy a marker then click to paste nearby</span>
          </div>
          <div className="footer-actions">
            <button onClick={handleClose} className="btn-done">
              ✅ Done ({markers.length} markers)
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarkerCreationModal;